import moment from 'moment';
import * as React from 'react';
import { CredentialAction } from '../actions/credential';
import { DashboardAction } from '../actions/dashboard';
import { LogSearchAction } from '../actions/logsearch';
import { SessionAction } from '../actions/session';
import { TicketAction } from '../actions/ticket';
import { RiskAction } from '../actions/risk';
import { ListIpsAction } from '../actions/listips';
import { State as CredentialState } from '../reducers/credential';
import { State as DashboardState } from '../reducers/dashboard';
import { State as LogSearchState } from '../reducers/logsearch';
import { State as LogSourceState } from '../reducers/logsources';
import { State as SessionState, TSecurityReadiness } from '../reducers/session';
import { State as RiskState } from '../reducers/risk';
import { State as OnboardingState } from '../reducers/onboarding';
import { State as ListIPsState } from '../reducers/listips';
import { LogSourceAction } from '../actions/logsources';
import { ThunkDispatch } from 'redux-thunk';
import { OnboardingAction } from '../actions/onboarding';
import { FormItemProps } from '@rtkwlf/ui';
import {
  OBCloudSensorTypes,
  OBHardwareType,
  OBServiceTypes,
  OBVirtualSensorTypes,
} from './onboarding';

export type Store = {
  credential: CredentialState;
  dashboard: DashboardState;
  session: SessionState;
  logSearch: LogSearchState;
  logSource: LogSourceState;
  risk: RiskState;
  onboarding: OnboardingState;
  listIps: ListIPsState;
};

export type Action =
  | CredentialAction
  | DashboardAction
  | LogSearchAction
  | LogSourceAction
  | SessionAction
  | TicketAction
  | RiskAction
  | ListIpsAction
  | OnboardingAction;

// this is different than the original Dispatch interface defined by redux
export type Dispatch = ThunkDispatch<Store, null, Action>;

export type TooltipPlacement =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

export type UserRole =
  | 'contact_cse'
  | 'credsmgmt'
  | 'custswitch'
  | 'mspswitch'
  | 'dashboard'
  | 'dataview'
  | 'rawlogs'
  | 'ticket'
  | 'onboarding'
  | 'reports';

export enum UserRoleEnum {
  VIEW_CONTACT_CSE = 'contact_cse',
  VIEW_CREDS_MGMT = 'credsmgmt',
  VIEW_CUST_SWITCH = 'custswitch',
  VIEW_DASHBOARD = 'dashboard',
  VIEW_DATA = 'dataview',
  VIEW_MSPSWITCH = 'mspswitch',
  VIEW_LOG_SEARCH = 'rawlogs',
  VIEW_TICKETS = 'ticket',
  VIEW_ONBOARDING = 'onboarding',
  VIEW_REPORTS = 'reports',
}

export enum PermissionEnum {
  VIEW_DASHBOARD = 'VIEW_DASHBOARD',
  VIEW_DATA = 'VIEW_DATA',
  VIEW_LOG_SEARCH = 'VIEW_LOG_SEARCH',
  VIEW_TOP_COUNTRIES = 'VIEW_TOP_COUNTRIES',
  VIEW_REPORTS = 'VIEW_REPORTS',
}

// Common types
export type StartEndDateFunction = (
  start: moment.Moment,
  end: moment.Moment,
) => void;

export type InputState = {
  value: string;
  isValid: boolean;
};

export type FlowDataViewTypes =
  | 'device'
  | 'protocol'
  | 'location'
  | 'site'
  | 'sensor';

export type AdDataViewTypes = 'event_code' | 'user' | 'origin_user' | 'domain';

export type SensorEntry = {
  id: string;
  ip: string;
  configuration: string;
  shorthand: string;
  type: string;
  state: string;
  health: string;
  shipped_date?: string;
};

export type IPListAddress = {
  subnet: string;
  dns: string;
  ports: Array<string>;
};

export type IPListIntegration = {
  action: string;
  subnets: Array<string>;
};

export type AWS = {
  cspmAccount: string;
  cloudTrail: string;
  s3Logs: string;
};

export type IPListType = {
  aws: AWS;
  agent: Array<IPListAddress>;
  mr: Array<IPListAddress>;
  externalScans: Array<string>;
  mdr: Array<IPListAddress>;
  cloud: Array<string>;
  integrations: Array<IPListIntegration>;
  ma: Array<string>;
};

export type UserPod = {
  pod: string;
};

export type RawAWS = {
  CSPMAccount: string;
  CloudTrail: string;
  S3Logs: string;
};

export type RawIPListType = {
  AWS: RawAWS;
  Agent: Array<IPListAddress>;
  MR: Array<IPListAddress>;
  externalScans: Array<string>;
  MDR: Array<IPListAddress>;
  Cloud: Array<string>;
  Integrations: Array<IPListIntegration>;
  MA: Array<string>;
};

// Log Search API

export type LogSource = {
  name: string;
  timezone: string;
  parsers: Array<string>;
  tags: Array<string>;
};
export type LogSourceList = Array<LogSource>;

export type LogSourceTagUpdate = {
  tag: string;
  sources: Array<string>;
  action: 'append' | 'remove';
};

export type SplitTimeframeOptions = 'day';
export type ExportDataFormatOptions = 'csv';
export type ExportArchiveFormatOption = 'zip';

export type ExportRequest = {
  searchId: string;
  dataFormat?: ExportDataFormatOptions;
  archiveFormat?: ExportArchiveFormatOption;
  logSourcesInfo: Array<string> | null;
  splitByTimeframe: SplitTimeframeOptions | null;
};

export type LogSourcesTags = Record<string, string[]>;

export type SearchRequest = {
  logSourcesTags: LogSourcesTags; // tags to sources mapping
  query: string; // raw string query expression
  start: moment.Moment; // start of search range
  end: moment.Moment; // end of search range
  caseSensitive: boolean; // whether or not search should ignore case
};

export type Export = {
  id: string;
  searchId: string;
  percentComplete: number;
  status: ExportStatus;
};

export type Search = {
  request: SearchRequest;
  failed: boolean;
  id: string; // UUID of request
  user: string; // search initiator's full name
  date: moment.Moment; // time search was invoked
  percent: number; // percent completion (0.0 - 1.0)
  size: number; // uncompressed size of matching lines
  count: number; // number of matching lines
  availableCount: number; //contiguous matches from end of timeframe (eventually converges to 'count')
  searchExport: Export | null;
};

export enum ExportStatus {
  NOT_STARTED = 'NOT_STARTED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export type ExportJob = {
  id: string;
  searchId: string;
  status: ExportStatus;
  progressPercentage: number;
  fileCount: number;
  totalDownloadSize: number;
};

export type ExportResult = {
  status: ExportStatus;
  files: Array<ExportFile>;
};

export type ExportFile = {
  suggested_name: string;
  part_id: string;
  sequence_number: number;
};

export type DownloadLink = {
  link: string;
};

export type Histogram = Array<{ x: number; y: number }>;

export type LogLine = {
  timestamp: string;
  deployment: string;
  host: string;
  body: string;
};

export type SearchResults = {
  pageNum: number;
  loading: boolean;
  results: Array<LogLine> | null;
};

export type ActiveSearch = {
  search: Search;
  histogram: Histogram | null;
};

export type StatusColor = 'danger' | 'warning' | 'success' | 'info';

export type StatusType = {
  color: StatusColor;
  message: React.ReactNode;
  title?: string;
};

export type GlobalStatusType = StatusType & {
  serialNumber: number;
};

export type SwitchableCustomerType = {
  id: string;
  name: string;
  configuration_score?: number;
  security_score?: number;
  riskID?: string;
};

export type TeamMemberType = {
  name: string;
  role: string;
};

// to be deprecated in https://arcticwolf.atlassian.net/browse/AWN-36344
export enum ServicesEnum {
  MDR = 'csoc',
  MR = 'risk',
  LOGS = 'logs',
  COMPLIANCE = 'compliance',
  AWARENESS = 'awareness',
  AWARENESSPLUS = 'awarenessPlus',
}

export enum PartnerTypeEnum {
  NONE = 'none', //There's no value set in SFDC, or we haven't sync'd one.
  DIRECT = 'direct', // The account is managed directly by AWN
  MSP = 'msp', // The account is managed through an MSP.
  MSP_PLUS = 'msp_plus', // The account is managed through an MSP, plus some direct customer interactions.
}

export type DeviceQuantityMap = Record<string, number>;

export type OnboardingStatus = {
  can_edit_sections: boolean;
  tko_completed: boolean;
  handoff_completed: boolean;
  partially_completed: boolean;
  ready_for_handover_or_acceptance: boolean;
};

export type CustomerType = {
  all_users_can_comment: boolean;
  log_search_enabled: boolean;
  cst: Array<TeamMemberType>;
  id: string;
  riskID?: string | undefined;
  partner_type: PartnerTypeEnum;
  interesting_countries: Array<string>;
  name: string;
  sensors: Array<SensorEntry>;
  retention: number;
  security_readiness: TSecurityReadiness;
  services?: ServicesEnum[];
  is_onboarding: boolean;
  onboarding_status: OnboardingStatus;
  pod?: string;
};

export type UserType = {
  can_view_all_tickets: boolean;
  // Zendesk agent users may not have a customer
  customer?: CustomerType;
  email: string;
  name: string;
  roles: UserRoleEnum[];
  feature_flags: {
    [flag: string]: boolean | string[] | string;
  };
  msp_id?: string | null;
};

export type RawExport = {
  export_id: string;
  search_id: string;
  percent_complete: number;
  status: ExportStatus;
};

export type Scaled = {
  num: string;
  suffix: string;
};

export type YupValidatorType = {
  data_type: 'string'; // add more when it's supported
  validator_name: string;
  validator_value?: any;
  error_message?: string;
};

export type CredentialField = {
  name: string;
  display_name: string;
  description: string;
  sensitive: boolean;
  data_type: 'string' | 'json' | 'checkbox' | 'fieldchoice' | 'select';
  order: number; // not used by frontend, as the list should be sorted by server
  required: boolean;
  default: string;
  options: Array<string>;
  validators: Array<YupValidatorType>;
};

export enum SubscriptionType {
  LOG_SEARCH = 'logSearch',
  MDR = 'managedDetectionResponse',
  MR = 'managedRisk',
  COMPLIANCE = 'compliance',
  AWARENESS = 'awareness',
  AWARENESS_PLUS = 'awarenessPlus',
  CONTENT_PACK = 'contentPack',
  ASSURANCE = 'assurance',
  STORAGE = 'storage',
}

export type ManagedDetectionResponse = {
  physicalSensors?: {
    awn101?: number;
    awn201?: number;
    awn202?: number;
    awn301?: number;
    awn201_10g_copper?: number;
    awn201_10g_fiber?: number;
    awn201_10g_twinax?: number;
    awn202_10g_copper?: number;
    awn202_10g_fiber?: number;
    awn202_10g_twinax?: number;
    awn301_10g_copper?: number;
    awn301_10g_fiber?: number;
    awn301_10g_twinax?: number;
  };
  cloudSensors?: {
    aws?: number;
    azure?: number;
    o365?: number;
    salesforce?: number;
    box?: number;
    gsuite?: number;
  };
  virtualSensors?: {
    vlc?: number;
    vsensor?: number;
    awnv100?: number;
    awnv200?: number;
    awnv1000?: number;
  };
};

export type Subscriptions = {
  enabled: boolean;
  subscriptionType: SubscriptionType;
  managedDetectionResponse?: ManagedDetectionResponse;
  managedRisk?: {
    scanner100: number;
    scanner200: number;
  };
  compliance?: {};
  awareness?: {};
  awarenessPlus?: {};
  contentPack?: {};
  assurance?: {};
  storage?: {
    logRetention: number;
  };
  logSearch?: {};
};

export type CredentialSchemaPurpose = 'log' | 'scan' | 'auth' | 'itsm';

export type CredentialSchema = {
  name: string;
  display_name: string;
  image: string;
  no_charge: boolean;
  setup_guide: string;
  fields: Array<CredentialField>;
  purpose: CredentialSchemaPurpose;
};

export type CredentialRequest = {
  name: string;
  type: string;
  secrets: Record<string, string>;
};

export type CredentialSecret = CredentialRequest & {
  uuid: string;
  status: CredentialStatus;
  tokens?: Array<string>;
  ticket_id: string;
};

export type VirtualLogCollector = {
  short_name: string;
  serial_number: string;
  deployment?: string;
  deployment_type?: 'vlc' | 'mirroring' | 'internal_tap' | 'scanner';
  state: 'activated' | 'registered';
  product_type: 'vLC' | 'vSensor' | 'vsensor' | 'vlc' | 'scanner';
  hardware_type?: string;
  is_vanilla?: boolean;
  profile_id?: number;
  model_size?: string;
  site?: string;
};

export type ConfigurationEntry = {
  display_name: string;
  status: string;
};

export type License = {
  quota: number;
  used: number;
};

export type VirtualSensor = {
  vsensor?: License;
  vSensor?: License;
  awnv100?: License;
  awnv200?: License;
  awnv1000?: License;
  vlc?: License;
  vLC?: License;
  vanilla?: License;
  results?: VirtualLogCollector[];
};

export type StatusSection = {
  title: string;
  // if set, will replace `title` as the rendering content
  titleNode?: React.ReactNode;
  status?: 'success' | 'fail';
  children?: StatusSection[];
};

export type StatusPageObjects = StatusSection[];

export type SensorDeployment = {
  health: string;
  state: string;
};

export type SensorProfile = {
  deployment?: SensorDeployment;
  shorthand: string;
  deployment_type: string;
};

export type SiteSensorProfiles = {
  sites: Record<string, SensorProfile[]>;
  orphans: SensorProfile[];
};

// react-select
export type OptionType = {
  label: string;
  value: string;
  customElement?: React.ReactNode;
  isDisabled?: boolean;
};

export type BrowserType = {
  name: string;
  version: number;
};

export enum CredentialStatus {
  ACTIVE = 'active',
  DEACTIVATING = 'deactivating',
  CREATE = 'create',
  EDIT = 'edit',
  ERROR = 'error',
}

export enum SessionStorageKey {
  PRIMARY_TOKEN = 'primary_token',
  SECONDARY_TOKEN = 'secondary_token',
  AUTH_MESSAGE = 'authMessage',
  USE_AUTH0 = 'useAuth0',
  USE_AUTH0_FRONTEND = 'useAuth0Frontend',
  AUTH0_EMAIL = 'auth0Email',
  LOGIN_ERROR = 'loginError',
  IMPERSONATE = 'impersonate',
  LOGGED_OUT = 'loggedOut',
  LEGACY_LOGIN_PAGE = 'legacyLoginPage',
}

// From https://github.com/rtkwlf/salesforce/blob/7072d718e973d6cddd4269060550c1007d572fc4/api_spec.yaml#L394
export type SFProduct = {
  startDate?: string;
  expiry?: string;
  serviceType?: OBServiceTypes;
  hardwareType?: OBHardwareType;
  cloudSensorType?: OBCloudSensorTypes;
  virtualSensorType?: OBVirtualSensorTypes;
  hardwareSeries?: string;
  productName?: string;
  quantity?: number;
  logRetention?: number;
};

export type SFSubscriptions = {
  organizationId: string;
  lastUpdated?: string;
  products?: SFProduct[];
};

export type RefMap<K = string, R = HTMLDivElement> = Map<K, React.RefObject<R>>;

export type InputValidationObject = Pick<FormItemProps, 'status' | 'message'>;

// to catch errors on unexpected value types (will not support nested field path)
// https://github.com/jaredpalmer/formik/pull/2012
export type StrictSetFieldValue<Values> = <K extends keyof Values>(
  field: K,
  value: Values[K],
  shouldValidate?: boolean,
) => void;

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export * from './risk';

export * from './onboarding';
